import { useState } from 'react';
import type { CpcCalculationListItem } from './cpc-types';
import {
  useActualLogEntries,
  useLinkExistingProject,
  useCreateClockodoProject,
  useManualSync,
} from './cpc-hooks';

interface Props {
  calculation: CpcCalculationListItem;
  companyId: string;
  clockodoCustomerId?: number | null;
}

const btnStyle = (variant: 'primary' | 'secondary' | 'danger' = 'primary'): React.CSSProperties => ({
  padding: '0.35rem 0.85rem',
  border: '1px solid ' + (variant === 'danger' ? 'var(--color-danger, #dc3545)' : variant === 'secondary' ? 'var(--color-border)' : 'var(--color-primary)'),
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '0.8rem',
  background: variant === 'primary' ? 'var(--color-primary)' : 'transparent',
  color: variant === 'primary' ? '#fff' : variant === 'danger' ? 'var(--color-danger, #dc3545)' : 'var(--color-text)',
});

const inputStyle: React.CSSProperties = {
  padding: '0.35rem 0.6rem',
  border: '1px solid var(--color-border)',
  borderRadius: '4px',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: '0.875rem',
};

export function CpcClockodoPanel({ calculation, companyId, clockodoCustomerId }: Props) {
  const [linkMode, setLinkMode] = useState<'none' | 'existing' | 'create'>('none');
  const [projectId, setProjectId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [customersId, setCustomersId] = useState(clockodoCustomerId ? String(clockodoCustomerId) : '');
  const [error, setError] = useState('');
  const [showEntries, setShowEntries] = useState(false);

  const { data: entries = [], isLoading: entriesLoading } = useActualLogEntries(
    showEntries ? calculation.id : null,
  );

  const linkExisting = useLinkExistingProject(calculation.id, companyId);
  const createProject = useCreateClockodoProject(calculation.id, companyId);
  const manualSync = useManualSync(calculation.id, companyId);

  const isLinked = calculation.clockodoLinkStatus === 'linked';
  const isError = calculation.clockodoLinkStatus === 'sync_error';

  const handleLinkExisting = async () => {
    setError('');
    if (!projectId.trim()) { setError('Bitte Clockodo-Projekt-ID eingeben'); return; }
    try {
      await linkExisting.mutateAsync({ clockodoProjectId: projectId.trim() });
      setLinkMode('none');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const handleCreateProject = async () => {
    setError('');
    if (!projectName.trim()) { setError('Projektname fehlt'); return; }
    const custId = parseInt(customersId, 10);
    if (isNaN(custId)) { setError('Clockodo-Kunden-ID muss eine Zahl sein'); return; }
    try {
      await createProject.mutateAsync({ projectName: projectName.trim(), clockodoCustomersId: custId });
      setLinkMode('none');
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  const expectedPT = calculation.result?.totalPT?.expected ?? 0;
  const actualPT = calculation.actualPtCached ? Number(calculation.actualPtCached) : null;
  const burnPct = actualPT != null && expectedPT > 0 ? Math.min(100, (actualPT / expectedPT) * 100) : null;

  return (
    <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: 'var(--color-surface, var(--color-bg-alt, #f8f9fa))', borderRadius: '6px', border: '1px solid var(--color-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Clockodo</span>

        {isLinked && (
          <span style={{ fontSize: '0.75rem', color: 'var(--color-success, #198754)' }}>
            Projekt {calculation.clockodoProjectId} verknüpft
          </span>
        )}
        {isError && (
          <span style={{ fontSize: '0.75rem', color: 'var(--color-danger, #dc3545)' }}>
            Sync-Fehler — Projekt {calculation.clockodoProjectId}
          </span>
        )}
        {!isLinked && !isError && (
          <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>Nicht verknüpft</span>
        )}

        {isLinked && (
          <>
            <button style={btnStyle('secondary')} onClick={() => manualSync.mutate()} disabled={manualSync.isPending}>
              {manualSync.isPending ? 'Sync…' : 'Jetzt synchronisieren'}
            </button>
            <button style={btnStyle('secondary')} onClick={() => setShowEntries((v) => !v)}>
              {showEntries ? 'Einträge ausblenden' : 'Zeiteinträge anzeigen'}
            </button>
          </>
        )}

        {!isLinked && linkMode === 'none' && (
          <>
            <button style={btnStyle('secondary')} onClick={() => { setLinkMode('existing'); setError(''); }}>
              Bestehendes Projekt verknüpfen
            </button>
            <button style={btnStyle('secondary')} onClick={() => { setLinkMode('create'); setError(''); }}>
              Neues Clockodo-Projekt anlegen
            </button>
          </>
        )}
      </div>

      {linkMode === 'existing' && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            style={{ ...inputStyle, width: 180 }}
            placeholder="Clockodo-Projekt-ID"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          />
          <button style={btnStyle('primary')} onClick={handleLinkExisting} disabled={linkExisting.isPending}>
            {linkExisting.isPending ? 'Verknüpft…' : 'Verknüpfen + Sync'}
          </button>
          <button style={btnStyle('secondary')} onClick={() => setLinkMode('none')}>Abbrechen</button>
          {error && <span style={{ fontSize: '0.8rem', color: 'var(--color-danger, #dc3545)' }}>{error}</span>}
        </div>
      )}

      {linkMode === 'create' && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            style={{ ...inputStyle, width: 220 }}
            placeholder="Projektname in Clockodo"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />
          <input
            style={{ ...inputStyle, width: 160 }}
            placeholder="Clockodo Kunden-ID"
            type="number"
            value={customersId}
            onChange={(e) => setCustomersId(e.target.value)}
          />
          <button style={btnStyle('primary')} onClick={handleCreateProject} disabled={createProject.isPending}>
            {createProject.isPending ? 'Anlegt…' : 'Anlegen + Verknüpfen'}
          </button>
          <button style={btnStyle('secondary')} onClick={() => setLinkMode('none')}>Abbrechen</button>
          {error && <span style={{ fontSize: '0.8rem', color: 'var(--color-danger, #dc3545)' }}>{error}</span>}
        </div>
      )}

      {isLinked && burnPct !== null && (
        <BurnDownBar expectedPT={expectedPT} actualPT={actualPT!} burnPct={burnPct} lastSynced={calculation.lastSyncedAt} />
      )}

      {showEntries && (
        <div style={{ marginTop: '0.75rem' }}>
          {entriesLoading ? (
            <p style={{ fontSize: '0.8rem', margin: 0 }}>Lädt Zeiteinträge…</p>
          ) : entries.length === 0 ? (
            <p style={{ fontSize: '0.8rem', margin: 0, color: 'var(--color-text-muted)' }}>
              Keine Zeiteinträge aus Clockodo.
            </p>
          ) : (
            <TimeEntriesTable entries={entries} />
          )}
        </div>
      )}
    </div>
  );
}

function BurnDownBar({ expectedPT, actualPT, burnPct, lastSynced }: {
  expectedPT: number;
  actualPT: number;
  burnPct: number;
  lastSynced?: string | null;
}) {
  const barColor = burnPct >= 100 ? 'var(--color-danger, #dc3545)' : burnPct >= 80 ? '#f59e0b' : 'var(--color-success, #198754)';

  return (
    <div style={{ marginTop: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '0.25rem' }}>
        <span>Ist: <strong>{actualPT.toFixed(1)} PT</strong> von Plan: <strong>{expectedPT.toFixed(1)} PT</strong></span>
        <span style={{ color: 'var(--color-text-muted)' }}>
          {burnPct.toFixed(0)} % verbraucht
          {lastSynced && ` · Sync ${new Date(lastSynced).toLocaleString('de-DE')}`}
        </span>
      </div>
      <div style={{ height: 8, background: 'var(--color-border)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(burnPct, 100)}%`, background: barColor, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

function TimeEntriesTable({ entries }: { entries: import('./cpc-types').CpcActualLogEntry[] }) {
  const byRole = entries.reduce<Record<string, number>>((acc, e) => {
    acc[e.role] = (acc[e.role] ?? 0) + Number(e.pt);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.5rem', fontSize: '0.8rem' }}>
        {Object.entries(byRole).map(([role, pt]) => (
          <span key={role}><code>{role}</code>: <strong>{pt.toFixed(2)} PT</strong></span>
        ))}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border)', textAlign: 'left' }}>
            <th style={{ padding: '0.25rem 0.5rem' }}>Datum</th>
            <th style={{ padding: '0.25rem 0.5rem' }}>Rolle</th>
            <th style={{ padding: '0.25rem 0.5rem' }}>Stunden</th>
            <th style={{ padding: '0.25rem 0.5rem' }}>PT</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '0.25rem 0.5rem' }}>{new Date(e.date).toLocaleDateString('de-DE')}</td>
              <td style={{ padding: '0.25rem 0.5rem' }}><code>{e.role}</code></td>
              <td style={{ padding: '0.25rem 0.5rem' }}>{Number(e.hours).toFixed(2)}</td>
              <td style={{ padding: '0.25rem 0.5rem' }}>{Number(e.pt).toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
